import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { goToLogin } from '../../../../api/auth';
import { LinkModal } from '../../../../components/ATS/LinkModal/LinkModal';
import { SvgExternalLink } from '../../../../icons';
import { Asset } from '../../../../queries/ats/titles/types/asset';
import { ThirdPartyRelationSource, ThirdPartySource } from '../../../../queries/ats/titles/types/sources';
import { tracker } from '../../../../tracker';
import { ThirdPartyServiceOpenedEvent } from '../../../../tracker/events/third-party-service-opened';
import { localizedRelativeTime } from '../../../../utils/ats-details-utils';
import { useAuth } from '../../../../utils/auth-utils';
import { useDetailsSearchParams } from '../../use-details-search-params';
import { CallToAction } from '../call-to-action-button';
import { ThirdPartySourceDeepLink } from '../third-party-source-deep-link';
import { NotAvailableCallToAction } from './not-available-call-to-action';

interface ThirdPartyCatchupCallToActionProps {
  asset: Asset;
  source: ThirdPartySource | ThirdPartyRelationSource;
}

export function ThirdPartyCatchupCallToAction({ asset, source }: ThirdPartyCatchupCallToActionProps) {
  const { t } = useTranslation();
  const { isGuest } = useAuth();

  const link = source.links.open || source.links.play;

  const { page, section } = useDetailsSearchParams();

  const track = useCallback(() => {
    tracker.track(new ThirdPartyServiceOpenedEvent(asset, source, page, section));
  }, [asset, source, page, section]);

  if (isGuest) {
    return (
      <CallToAction
        text={{
          primary: t('details:get-access'),
        }}
        image={{
          src: source.image,
          alt: source.name,
        }}
        icon={<SvgExternalLink />}
        onClick={goToLogin}
      />
    );
  }

  if (link === undefined) {
    return <NotAvailableCallToAction source={source} />;
  }

  const startTime = 'startTime' in source ? localizedRelativeTime(source.startTime, 'airtime') : undefined;

  if (source.isSubscribed) {
    return (
      <LinkModal
        id={source.id}
        title={source.name}
        description={t(`thirdparty:popup_description_${source.popupTextType}`, { name: source.name })}
        primaryButton={{
          title: t('thirdparty:popup_continue_to', { name: source.name }),
          url: link,
          onClick: track,
        }}
      >
        <CallToAction
          text={{
            primary: source.isSubscribed ? `${t('details:open-in')} ${source.name}` : t('details:get-access'),
            secondary: startTime,
          }}
          image={{
            src: source.image,
            alt: source.name,
          }}
          icon={<SvgExternalLink />}
          href={link}
          onClick={track}
        />
      </LinkModal>
    );
  }

  return <ThirdPartySourceDeepLink asset={asset} source={source} subtitle={startTime} />;
}
